import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { loadStripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import { STRIPE_PUBLISHABLE_KEY } from '@/lib/constants';
import { useVenueContext } from '@/providers/VenueProvider';
import { useBooking } from '@/api/hooks/useBookings';
import { useCreditBalance } from '@/api/hooks/useCredit';
import { useAuth } from '@/providers/AuthProvider';
import { formatCurrency, formatLocalTime } from '@/lib/format';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import UnlockDialog from '@/components/booking/UnlockDialog';
import ExtendDialog from '@/components/session/ExtendDialog';
import { Clock, ChevronLeft } from 'lucide-react';

const stripePromise = loadStripe(STRIPE_PUBLISHABLE_KEY);

function formatCountdown(totalSeconds: number): string {
  if (totalSeconds <= 0) return '0:00';
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  if (hours > 0) {
    return `${hours}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  }
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
}

function SessionContent() {
  const { id } = useParams<{ id: string }>();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const { currentSlug } = useVenueContext();
  const [secondsRemaining, setSecondsRemaining] = useState<number | null>(null);
  const [extendDialogOpen, setExtendDialogOpen] = useState(false);
  const [extendMinutes, setExtendMinutes] = useState(30);

  const { data, isLoading, error } = useBooking(id || '');
  const { data: creditData } = useCreditBalance(isAuthenticated);

  const booking = data?.booking;
  const access = booking?.access;
  const isActive = booking?.status === 'ACTIVE';
  const isConfirmed = booking?.status === 'CONFIRMED';
  const sessionLive = isActive || isConfirmed;
  const creditBalance = creditData?.balance || 0;

  // Price per hour for extension estimate
  const pricePerHour = booking?.pricing?.unitPrice || 0;
  const currency = booking?.pricing?.currency || 'CAD';

  // Countdown timer
  useEffect(() => {
    if (!booking?.endTime) return;
    const endMs = new Date(booking.endTime).getTime();

    const tick = () => {
      const remaining = Math.max(0, Math.floor((endMs - Date.now()) / 1000));
      setSecondsRemaining(remaining);
      if (remaining <= 0) clearInterval(interval);
    };
    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [booking?.endTime]);

  const sessionEnded = secondsRemaining !== null && secondsRemaining <= 0 && isActive;

  const handleExtendClick = (minutes: number) => {
    setExtendMinutes(minutes);
    setExtendDialogOpen(true);
  };

  if (isLoading) {
    return (
      <div className="mx-auto max-w-md space-y-6 px-4 py-8">
        <Skeleton className="mx-auto h-32 w-48" />
        <Skeleton className="h-40 w-full" />
      </div>
    );
  }

  if (error || !booking) {
    return (
      <div className="mx-auto max-w-md px-4 py-8">
        <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4 text-sm text-destructive">
          {t('common.error')}
        </div>
      </div>
    );
  }

  const extensionPrice = pricePerHour * (extendMinutes / 60);

  return (
    <div className="mx-auto max-w-md space-y-6 px-4 py-6">
      {/* Back */}
      <button
        onClick={() => navigate(`/bookings/${booking.id}`)}
        className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
      >
        <ChevronLeft className="h-4 w-4" />
        {t('session.view_booking')}
      </button>

      {/* Sport + time header */}
      <div className="text-center">
        <p className="text-lg font-semibold">{t(`sport_${booking.sportCode}`, { defaultValue: booking.sportName })}</p>
        <p className="text-sm text-muted-foreground">
          {booking.unitName} · {formatLocalTime(booking.startTimeLocal, i18n.language)} → {formatLocalTime(booking.endTimeLocal, i18n.language)}
        </p>
      </div>

      {/* Countdown timer */}
      <Card className={sessionEnded ? 'bg-muted' : 'bg-gradient-to-b from-primary/10 to-background border-primary/30'}>
        <CardContent className="space-y-2 p-8 text-center">
          {sessionEnded ? (
            <>
              <Clock className="mx-auto h-8 w-8 text-muted-foreground" />
              <p className="text-2xl font-bold text-muted-foreground">
                {t('session.session_ended')}
              </p>
            </>
          ) : (
            <>
              <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
                <Clock className="h-4 w-4" />
                <span>{t('session.time_remaining')}</span>
              </div>
              <p className={`font-mono font-bold tracking-wider ${
                secondsRemaining !== null && secondsRemaining < 300
                  ? 'text-6xl text-red-500 animate-pulse'
                  : 'text-6xl'
              }`}>
                {secondsRemaining !== null ? formatCountdown(secondsRemaining) : '--:--'}
              </p>
            </>
          )}
        </CardContent>
      </Card>

      {/* Access code */}
      {access && sessionLive && !sessionEnded && (
        <Card className="border-2 border-primary bg-primary/5">
          <CardContent className="space-y-3 p-6 text-center">
            <p className="text-sm uppercase tracking-wide text-muted-foreground">
              {t('booking.access_code')}
            </p>
            <p className="text-4xl font-mono font-bold tracking-[0.3em]">
              {access.code}
            </p>
            {access.code && (
              <UnlockDialog
                accessCode={access.code}
                bookingStartTime={booking.startTime}
                startTimeLocal={booking.startTimeLocal}
                unlockAvailableAt={access.unlockAvailableAt}
                unitName={booking.unitName}
              />
            )}
          </CardContent>
        </Card>
      )}

      {/* Extend buttons — show price estimate */}
      {sessionLive && !sessionEnded && (
        <div className="flex gap-3">
          {[30, 60].map((minutes) => (
            <Button
              key={minutes}
              variant="outline"
              className="flex-1 whitespace-nowrap"
              onClick={() => handleExtendClick(minutes)}
            >
              {t('session.extend_by', { minutes })}
              <span className="ml-1 text-xs text-muted-foreground">
                ~{formatCurrency(pricePerHour * (minutes / 60), currency, i18n.language)}
              </span>
            </Button>
          ))}
        </div>
      )}

      {/* Session ended actions */}
      {sessionEnded && (
        <div className="flex flex-col gap-3">
          <Button className="whitespace-nowrap" onClick={() => navigate(`/bookings/${booking.id}`)}>
            {t('session.view_booking')}
          </Button>
          <Button
            variant="outline"
            className="whitespace-nowrap"
            onClick={() => navigate(`/venue/${currentSlug}/book`)}
          >
            {t('booking.book_another')}
          </Button>
        </div>
      )}

      {/* Extend dialog */}
      <ExtendDialog
        open={extendDialogOpen}
        onOpenChange={setExtendDialogOpen}
        bookingId={booking.id}
        extensionMinutes={extendMinutes}
        extensionPrice={extensionPrice}
        creditBalance={creditBalance}
        currency={currency}
      />
    </div>
  );
}

export default function SessionPage() {
  const { i18n } = useTranslation();

  return (
    <Elements
      stripe={stripePromise}
      options={{ locale: i18n.language === 'fr' ? 'fr' : 'en' }}
    >
      <SessionContent />
    </Elements>
  );
}
